import React, { useState } from "react";
import {
    Box,
    Grid,
    TextField,
    InputAdornment,
    Typography,
    Button,
    IconButton,
    Stepper, Step, StepLabel, Card, CardContent, Dialog, DialogContent, DialogTitle, Stack, DialogActions,
    FormControl, FormControlLabel, Switch, Divider, CardActions
} from "@mui/material";
import InfoButton from "../../Tooltip/InfoTextField";
import { ReturnCandidateViewModel, SalesLineViewModel, ReturnModeRequest, ReturnViewModel, PreviousReturnViewModel } from "../../../Types/ExportTypes";
import { useTranslation } from "react-i18next";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CheckCircle from "@mui/icons-material/CheckCircle";
import LocalizedNumberInputs from "../../../components/Input/LocalizedNumberInputs"
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../../Context/Contexts";
import { ReturnReasonAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import ImageBox from "../../../components/Image/ImageBox";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
type Props = {
    viewModel: ReturnCandidateViewModel | null;
    onSave: (vm: ReturnCandidateViewModel) => void;
    cancelWizard: () => void;
};

const ReturnWizard: React.FC<Props> = ({ viewModel, onSave, cancelWizard }) => {
    const { t } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
    const { navigate } = useNavigation();
    const [localViewModel, setLocalViewModel] = useState<ReturnCandidateViewModel>(viewModel ?? {});
    const [step, setStep] = useState(0);
    const [returnMode, setReturnMode] = useState<ReturnModeRequest[]>([]);
    const [editingLine, setEditingLine] = useState<ReturnModeRequest | null>(null);
    const [readyToReturn, setReadyToReturn] = useState<boolean>(false);
    const [showOldreturns, setShowOldReturns] = useState<boolean>(false);
    const [returnFreight, setReturnFreight] = useState<number>(0);
    const [editingMode, setEditingMode] = useState<boolean>(false);
    const handleChange = (
        field: keyof ReturnModeRequest | Partial<ReturnModeRequest>,
        value?: any
    ) => {

        setEditingLine(prev => {
            let updated: ReturnModeRequest;

            if (typeof field === "object") {
                updated = { ...prev, ...field };
            } else {
                updated = { ...prev, [field]: value };
            }

            const newQty = typeof field === "object" ? field.quantity ?? updated.quantity : field === "quantity" ? value : updated.quantity;

            if (newQty !== undefined && updated.refundableAmount !== undefined) {
       
                const totalReturnableQuantity =
                    localViewModel.returnableLines?.find(l => l.id === updated.salesInvoiceLineId && l.returnLineGuid === updated.gUID)?.quantity ??
                    localViewModel.returnableLines?.find(l => l.id === updated.salesInvoiceLineId && (l.returnLineGuid == null || l.returnLineGuid == undefined))?.quantity ??
                    1;
                const calculatedRefund = +((updated.refundableAmount ?? 0) / totalReturnableQuantity * newQty).toFixed(2);

                if (updated.refundAmount === undefined || calculatedRefund < (updated.refundAmount ?? 0)) {
                    updated.refundAmount = calculatedRefund;
                }
            }

            return updated;
        });
    };


    const handleRow = (row: SalesLineViewModel) => {



        const existing = returnMode.find(r => r.salesInvoiceLineId === row.id && r.gUID == row.returnLineGuid);

        if (existing) {
            setEditingLine(existing);
        } else {
            const newRequest: ReturnModeRequest = {
                salesInvoiceLineId: row.id,
                salesInvoiceLineNo: row.lineNo,
                quantity: row.quantity - (row.creditedQuantity ?? 0),
                gUID: crypto.randomUUID(),
                itemNo: row.itemNo,
                barcode: row.barcode,
                description: row.description,
                colour: row.colourDescription,
                size: row.sizeDescription,
                length: row.lengthDescription,
                returnableQuantity: row.quantity - (row.creditedQuantity ?? 0),
                refundableAmount: (row.amountIncludingVat ?? 0) - (row.refundedAmount ?? 0),
                variantId: row.variantId,
                refundAmount: row.amountIncludingVat,
                img_Url: row.img_Url,
                totalReturnableQuantity: row.quantity - (row.creditedQuantity ?? 0)
                // fill in defaults as needed
            };
            setEditingLine(newRequest);
        }
        setEditingMode(true);
    };


    const saveRow = (addAnother: boolean = false) => {
        if (!editingLine || !validateReturnLine(editingLine)) return;

        const isHandled = (editingLine.quantity ?? 0) > 0;
        editingLine.handled = isHandled;

        const baseLines = localViewModel.returnableLines ?? [];
        const newReturnMode = updateReturnMode(editingLine, isHandled);
        setReturnMode(newReturnMode);

        const groupedReturnSummary = groupReturnSummary(newReturnMode);
        const updatedLines = buildUpdatedLines(baseLines, newReturnMode, groupedReturnSummary, editingLine, isHandled);

        const { remainingQty, remainingAmount } = tryPushUntouchedClone(updatedLines, baseLines, groupedReturnSummary, editingLine);

        setLocalViewModel(prev => ({ ...prev, returnableLines: updatedLines }));
        setReadyToReturn(newReturnMode.length > 0);

        if (addAnother && remainingQty > 0 && remainingAmount > 0) {
            const unhandledLine = updatedLines.find(
                l => l.id === editingLine.salesInvoiceLineId && !l.handled && l.returnLineGuid == null
            );

            if (unhandledLine) {
                showLoading();
                const newRequest = buildNewReturnRequest(unhandledLine, remainingQty, remainingAmount);
                setEditingLine(newRequest);
                setEditingMode(true);
                hideLoading();
                return;
            }
        }

        setEditingLine(null);
        setEditingMode(false);
    };

    const updateReturnMode = (line: ReturnModeRequest, isHandled: boolean): ReturnModeRequest[] => {
        const originalReturnableQty = line.returnableQuantity ?? line.quantity ?? 0;
        const currentQty = line.quantity ?? 0;

        const updated: ReturnModeRequest = {
            ...line,
            returnableQuantity: currentQty,
            totalReturnableQuantity: currentQty,
            ...(currentQty < originalReturnableQty && originalReturnableQty > 0
                ? {
                    refundableAmount: (line.refundableAmount ?? 0) / originalReturnableQty * currentQty
                }
                : {})
        };

        const exists = returnMode.find(r => r.gUID === line.gUID);
        if (isHandled) {
            return exists
                ? returnMode.map(r => r.gUID === line.gUID ? updated : r)
                : [...returnMode, updated];
        }

        return returnMode.filter(r => r.gUID !== line.gUID);
    };

    const groupReturnSummary = (mode: ReturnModeRequest[]) => {
        return mode.reduce<Record<number, { quantity: number; amount: number }>>((acc, r) => {
            if (!r.salesInvoiceLineId) return acc;
            const prev = acc[r.salesInvoiceLineId] ?? { quantity: 0, amount: 0 };
            acc[r.salesInvoiceLineId] = {
                quantity: prev.quantity + (r.quantity ?? 0),
                amount: prev.amount + (r.refundAmount ?? 0),
            };
            return acc;
        }, {});
    };

    const buildUpdatedLines = (
        baseLines: SalesLineViewModel[],
        newReturnMode: ReturnModeRequest[],
        groupedReturnSummary: Record<number, { quantity: number; amount: number }>,
        editingLine: ReturnModeRequest,
        isHandled: boolean
    ): SalesLineViewModel[] => {
        const updated: SalesLineViewModel[] = [];

        for (const line of baseLines) {
            const isSameId = line.id === editingLine.salesInvoiceLineId;
            const summary = groupedReturnSummary[line.id];
            const baseLine = baseLines.find(b => b.id === line.id);

            const maxReturnableQty = Math.max((baseLine?.quantity ?? 0) - (baseLine?.creditedQuantity ?? 0), 0);
            const remainingQty = Math.max(maxReturnableQty - (summary?.quantity ?? 0), 0);
            const remainingAmount = Math.max((baseLine?.amountIncludingVat ?? 0) - (summary?.amount ?? 0), 0);

            const isEditingTarget = line.returnLineGuid === editingLine.gUID;
            const isUntouchedClone = isSameId && !line.handled && line.returnLineGuid == null;
            const isUsed = newReturnMode.some(r => r.salesInvoiceLineId === line.id && r.gUID === line.returnLineGuid);
            const shouldRemoveClone = isUntouchedClone && isUsed && (editingLine.quantity ?? 0) > 0;
            const shouldRemoveEditingTarget = isEditingTarget && (editingLine.quantity ?? 0) === 0;

            if (shouldRemoveClone || shouldRemoveEditingTarget) continue;

            const shouldUpdate = (isEditingTarget || (isSameId && line.returnLineGuid == null)) && editingLine.quantity != 0;

            updated.push({
                ...line,
                ...(shouldUpdate ? {
                    quantity: editingLine.quantity ?? 0,
                    amountIncludingVat: editingLine.refundAmount ?? 0,
                    handled: isHandled,
                    returnLineGuid: editingLine.gUID,
                    remainingQty: editingLine.quantity ?? 0,
                } : {}),
                remainingQty,
                remainingAmount
            });
        }

        return updated;
    };

    const tryPushUntouchedClone = (
        updatedLines: SalesLineViewModel[],
        baseLines: SalesLineViewModel[],
        groupedReturnSummary: Record<number, { quantity: number; amount: number }>,
        editingLine: ReturnModeRequest
    ) => {
        const baseLinesForId = baseLines.filter(l => l.id === editingLine.salesInvoiceLineId);
        const credited = baseLinesForId[0]?.creditedQuantity ?? 0;
        const returnSummary = groupedReturnSummary[editingLine.salesInvoiceLineId!] ?? { quantity: 0, amount: 0 };

        const baseQty = baseLinesForId.reduce((sum, l) => sum + (l.quantity ?? 0), 0);
        const baseAmount = baseLinesForId.reduce((sum, l) => sum + (l.amountIncludingVat ?? 0), 0);
        const maxReturnableQty = Math.max(baseQty - credited, 0);

        const remainingQty = Math.max(maxReturnableQty - returnSummary.quantity, 0);
        const remainingAmount = Math.max(baseAmount - returnSummary.amount, 0);

        const existsUntouched = updatedLines.some(
            l => l.id === editingLine.salesInvoiceLineId && !l.handled && l.returnLineGuid == null
        );

        if (remainingQty > 0 && !existsUntouched) {
            const latest = [...baseLinesForId].at(-1);
            if (latest) {
                updatedLines.push({
                    ...latest,
                    handled: false,
                    returnLineGuid: null,
                    quantity: remainingQty,
                    amountIncludingVat: remainingAmount,
                    lineDiscountAmountIncVAT: 0,
                    remainingQty,
                    remainingAmount
                });
            }
        }

        return { remainingQty, remainingAmount };
    };

    const buildNewReturnRequest = (
        line: SalesLineViewModel,
        remainingQty: number,
        remainingAmount: number
    ): ReturnModeRequest => {
        return {
            salesInvoiceLineId: line.id,
            salesInvoiceLineNo: line.lineNo,
            quantity: remainingQty,
            gUID: crypto.randomUUID(),
            itemNo: line.itemNo,
            barcode: line.barcode,
            description: line.description,
            colour: line.colourDescription,
            size: line.sizeDescription,
            length: line.lengthDescription,
            returnableQuantity: remainingQty,
            refundableAmount: line.remainingAmount,
            variantId: line.variantId,
            refundAmount: remainingAmount,
            img_Url: line.img_Url,
            totalReturnableQuantity: line.quantity ?? remainingQty
        };
    };


    const validateReturnLine = (line: ReturnModeRequest) => {
        if ((line.quantity ?? 0) > (line.returnableQuantity ?? 0)) return false;
        return true;
    }
    const handleFinish = async () => {
        if (!validateViewModel()) return;
        showLoading();
        try {
            const response = await authService.post<ReturnViewModel>(
                `/salesReturnApi/CreateReturn`,
                { invoiceNo: localViewModel.invoiceNo, returnFreight: returnFreight, returnLines: returnMode }
            );
            showSnackbar(t("dialog.successCreatedReturn"), "success");
            cancelWizard();
            navigate({ path: "sales/return/edit", params: { returnNo: response.returnNo, history: false } });
        } catch (error) {
            showSnackbar(t("dialog.failedToCreateReturn"), "error");
        } finally {
            hideLoading();
        }

    };

    const validateViewModel = () => {
        //if (!localViewModel.description?.trim()) {
        //    showSnackbar(t("help.descriptionRequired"), "warning");
        //    return false;
        //}
        return true;

    }

    const validateStepAndNavigate = (newStep: number) => {
        if (newStep < 0) {
            setStep((s) => s + newStep)
            return;
        }

        if (step === 1 && !readyToReturn) {
            showSnackbar(t("dialog.invalidReturn"), "warning");
            return;
        }

        if (step + newStep === 2) {
            calculateReturnFreight();
        }
        setStep((s) => s + 1)
    }

    const calculateReturnFreight = async () => {
        showLoading();
        try {
            const response = await authService.post<{ returnFreight: number | null }>(
                `/salesReturnApi/CalcReturnFreight`,
                { invoiceNo: localViewModel.invoiceNo, returnLines: returnMode }
            );
            setReturnFreight(response.returnFreight ?? 0);
        }
        catch (error: any) {
        }
        finally {
            hideLoading();
        }

    }

    const navigateToReturn = async (previousReturn: PreviousReturnViewModel) => {
        const isConfirmed = window.confirm(t("dialog.areYouSure"));
        if (!isConfirmed) return;

        if (previousReturn.isCredit) {
            navigate({ path: "/sales/history/document", params: { documentNo: previousReturn.number, documentType: 3 } });
            return;
        }
        navigate({ path: "sales/return/edit", params: { returnNo: previousReturn.number, history: previousReturn.completed } });
    }

    function generateVariantLabel(line: SalesLineViewModel) {
        if (!line.variantId) {
            return "";
        }

        const parts = [];


        if (line.colourCode) {
            parts.push(line.colourCode);
        }

        if (line.colourDescription) {
            parts.push(line.colourDescription);
        }


        if (line.sizeDescription) {
            parts.push(line.sizeDescription);
        }


        if (line.lengthDescription) {
            parts.push(line.lengthDescription);
        }


        const details = parts.join(" | ");


        return `${line.variantId} | ${details || "No Details Available"}`;
    }


    function generateVariantLabelFromReturnCandidate(line?: ReturnModeRequest | null) {
        if (!line) return "";
        if (!line?.variantId) {
            return "";
        }

        const parts = [];



        if (line?.colour) {
            parts.push(line.colour);
        }


        if (line?.size) {
            parts.push(line.size);
        }


        if (line?.length) {
            parts.push(line.length);
        }


        const details = parts.join(" | ");


        return `${line.variantId} | ${details || "No Details Available"}`;
    }

    return (
        <>
            <Dialog open={editingMode} onClose={() => { setEditingLine(null); setEditingMode(false) }}>
                <DialogTitle sx={{ textAlign: "center" }}>{t("common.returnLine")}</DialogTitle>
                <DialogContent>
                    {editingLine && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>


                                    <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                        <ImageBox
                                            imageUrl={editingLine?.img_Url ?? ""}
                                            altText={editingLine?.description || "No Description"}
                                            fallbackText={t("common.noImage")}
                                            width="120px"
                                            height="120px"

                                        />
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t("common.number")}
                                    value={editingLine?.itemNo}

                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    disabled={true}

                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label={t("common.description")}
                                    value={editingLine?.description}

                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    margin="normal"
                                    disabled={true}

                                />
                            </Grid>

                            {editingLine?.variantId && (
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("common.variant")}
                                        value={generateVariantLabelFromReturnCandidate(editingLine)}

                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        margin="normal"
                                        disabled={true}

                                    />
                                </Grid>
                            )}
                            <Grid item lg={6} xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.quantity")}
                                    value={editingLine?.quantity ?? 1}
                                    onValueChange={(value) => {
                                        handleChange("quantity", value ?? 0);
                                    }}
                                    error={editingLine?.quantity == null || editingLine.quantity < 0 || editingLine.quantity > (editingLine?.returnableQuantity ?? 0)}
                                    helperText={editingLine?.quantity == null || editingLine.quantity < 0 ? t("help.quantityRequired") : (editingLine.quantity > (editingLine?.returnableQuantity ?? 0) ? t("help.quantityHigherThanReturnable") : "")}
                                    required
                                    allowNegative={false} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                />
                            </Grid>
                            <Grid item lg={6} xs={12}>
                                <FormControl fullWidth margin="normal">
                                    <ReturnReasonAutoComplete
                                        key={editingLine?.returnReason}
                                        label={t("common.returnReason")}
                                        valueId={editingLine?.returnReason}
                                        onChange={(selected) => {
                                            handleChange({
                                                returnReason: selected?.value || null,
                                                returnReasonDesc: selected?.description || null,
                                            });
                                        }}
                                        load={true}

                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.refundAmount")}
                                    value={editingLine?.refundAmount ?? 0}
                                    onValueChange={(value) => {
                                        handleChange("refundAmount", value ?? 0);
                                    }}
                                    required
                                    allowNegative={false} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                />
                            </Grid>
                            <Grid item xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.remainingAmount")}
                                    value={((editingLine?.refundableAmount ?? 0) - (editingLine?.refundAmount ?? 0))}
                                    onValueChange={(value) => {

                                    }}
                                    disabled={true}
                                    allowNegative={true} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals

                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" color="success" onClick={() => saveRow(false)}>{editingLine?.handled ? t("common.save") : t("common.add")}</Button>
                    {editingLine && (editingLine.returnableQuantity ?? 0) > (editingLine.quantity ?? 0) && (
                        <Button variant="contained" color="info" onClick={() => saveRow(true)}>{t("common.addSplitLine")}</Button>
                    )}
                    <Button onClick={() => { setEditingLine(null); setEditingMode(false) }}>{t("common.cancel")}</Button>

                </DialogActions>
            </Dialog>

            <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", px: 2 }}>
                <Stepper activeStep={step} sx={{ mb: 3, overflowX: "auto" }} alternativeLabel>
                    <Step><StepLabel> {t("common.info")}</StepLabel></Step>
                    <Step><StepLabel> {t("common.chooseRowsToReturn")}</StepLabel></Step>
                    <Step><StepLabel> {t("common.finish")}</StepLabel></Step>
                    {/*   <Step><StepLabel>Terminal</StepLabel></Step>*/}
                </Stepper>


                <Grid container spacing={2}>
                    {step === 0 && (<>
                        <Grid item xs={12} lg={6}>
                            <TextField
                                label={t("common.invoice")}
                                value={viewModel?.invoiceNo}

                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                disabled={true}

                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <TextField
                                label={t("common.order")}
                                value={viewModel?.orderNo}

                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                disabled={true}

                            />
                        </Grid>
                        <Grid item xs={12} lg={6} >
                            <TextField
                                label={t("common.name")}
                                value={viewModel?.billToName}

                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                disabled={true}

                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <TextField
                                label={t("common.address")}
                                value={viewModel?.billToAdress}

                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                disabled={true}

                            />
                        </Grid>

                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                {t("common.payments")}
                            </Typography>
                        </Grid>
                        {/* Card Layout for Orders */}
                        <Grid container spacing={2}>
                            {viewModel?.refundablePayments?.map((payment, i) => (
                                <Grid item
                                    xs={12}
                                    sm={viewModel?.refundablePayments?.length === 1 ? 12 : viewModel?.refundablePayments?.length === 2 ? 6 : 6}
                                    md={viewModel?.refundablePayments?.length === 1 ? 12 : viewModel?.refundablePayments?.length === 2 ? 6 : 4}
                                    key={payment.id}>
                                    <Card

                                        sx={{
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 4,
                                                backgroundColor: "secondary.main",
                                            },
                                        }}
                                    >
                                        <CardContent>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {payment.paymentMethodDescription}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.amount")}: {payment.amount}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.refunded")}: {payment.refundedAmount ?? 0}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.trans")}:{payment.trans}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                            ))}
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={showOldreturns} onChange={(e) => setShowOldReturns(e.target.checked)} />}
                                label={t("common.showOtherReturns")}
                            />
                        </Grid>
                        {showOldreturns && (
                            <>

                                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                        {t("common.returns")}
                                    </Typography>
                                </Grid>
                                {viewModel?.hasPreviousReturns && (

                                    <Grid container spacing={2}>

                                        {viewModel?.previousReturnNumbers?.map((item, i) => (
                                            <Grid item
                                                xs={12}
                                                key={item.number}>
                                                <Card

                                                    sx={{
                                                        cursor: "pointer",
                                                        transition: "box-shadow 0.2s ease-in-out",
                                                        "&:hover": {
                                                            boxShadow: 4,
                                                            backgroundColor: "secondary.main",
                                                        },
                                                    }}
                                                >
                                                    <CardContent>
                                                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                            {item.number}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("common.posted")}: {item.completed ? t("common.yes") : t("common.no")}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            {t("common.returnStatus")}: {t("common.returnStatus_" + (item.status ?? ""))}
                                                        </Typography>
                                                    </CardContent>
                                                    <Divider sx={{ mt: 0 }} />
                                                    <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                                                        <Button
                                                            component="a"
                                                            onClick={() => navigateToReturn(item)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            variant="contained"
                                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                                        >
                                                            {t("common.open")}
                                                        </Button>
                                                    </CardActions>
                                                </Card>
                                            </Grid>

                                        ))}
                                    </Grid>
                                )}
                            </>
                        )}
                    </>)}
                    {step === 1 && (<>

                        {/* Card Layout for Orders */}
                        <Grid container spacing={2}>
                            {localViewModel?.returnableLines?.map((line, i) => (
                                <Grid item
                                    xs={12}
                                    key={line.id}>
                                    <Card
                                        onClick={() => handleRow(line)}
                                        sx={{
                                            position: "relative",
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 4,
                                                backgroundColor: "secondary.main",
                                            },
                                        }}
                                    >

                                        {line.handled && (
                                            <Box position="absolute" top={8} right={8} color="success.main">
                                                <CheckCircle />
                                            </Box>
                                        )}

                                        <CardContent>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>


                                                <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                    <ImageBox
                                                        imageUrl={line.img_Url ?? ""}
                                                        altText={line.description || "No Description"}
                                                        fallbackText={t("common.noImage")}
                                                        width="120px"
                                                        height="120px"

                                                    />
                                                </Box>
                                            </Box>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {line.no}
                                            </Typography>

                                            <Typography variant="body2" color="textSecondary">
                                                {line.description}         {generateVariantLabel(line)}
                                            </Typography>

                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.amount")}: {line.amountIncludingVat} | {t("common.quantity")}: {line.quantity} |
                                                taco: {line.remainingAmount} | nachos: {line.remainingQty}
                                                | guid: {line.returnLineGuid}
                                            </Typography>


                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.alreadyReturnedQuantity")}: {line.creditedQuantity ?? 0}
                                            </Typography>

                                        </CardContent>
                                    </Card>
                                </Grid>

                            ))}
                            {localViewModel?.returnableLines?.length == 0 && !isLoading && (
                                <Grid item xs={12}>
                                    <Typography align="center">{t("common.noRowsLeftToReturn")}</Typography>
                                </Grid>
                            )}
                        </Grid>
                    </>)}
                    {step === 2 && (<>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.returnFreight")}
                                    value={returnFreight}
                                    onValueChange={(value) => {
                                        setReturnFreight(value ?? 0);
                                    }}

                                    required
                                    allowNegative={false} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                />
                            </Grid>
                            {returnMode?.map((item, i) => (
                                <Grid item
                                    xs={12}
                                    key={item.gUID}>
                                    <Card

                                        sx={{
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 4,
                                                backgroundColor: "secondary.main",
                                            },
                                        }}
                                    >
                                        <Grid item xs={12}>
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>


                                                <Box sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}>
                                                    <ImageBox
                                                        imageUrl={item?.img_Url ?? ""}
                                                        altText={item?.description || "No Description"}
                                                        fallbackText={t("common.noImage")}
                                                        width="120px"
                                                        height="120px"

                                                    />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {item.itemNo}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {item.description}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {generateVariantLabelFromReturnCandidate(item)}
                                            </Typography>

                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.amount")}: {item.refundAmount}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.quantity")}: {item.quantity}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {t("common.returnReason")}: {item.returnReasonDesc ?? item.returnReason}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                            ))}
                            <Grid item xs={12}>
                                <LocalizedNumberInputs
                                    label={t("common.amountToRefund")}
                                    value={returnMode.reduce((sum, item) => sum + (item.refundAmount ?? 0), 0) - returnFreight}
                                    onValueChange={(value) => {

                                    }}
                                    disabled={true}
                                    required
                                    allowNegative={true} // Prevents negative numbers
                                    decimalScale={4} // Ensures max 4 decimals
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}

                                />
                            </Grid>
                        </Grid>

                    </>)}
                </Grid>

                <Box sx={{ marginTop: 2, textAlign: "center" }}>

                    {step < 2 ? (
                        <Button fullWidth variant="contained" onClick={() => validateStepAndNavigate(1)}> {t("common.next")}</Button>
                    ) : readyToReturn && (
                        <Button fullWidth variant="contained" color="primary" onClick={handleFinish}>{t("common.finish")}</Button>
                    )}
                    <Button fullWidth disabled={step === 0} onClick={() => validateStepAndNavigate(-1)}> {t("common.back")}</Button>
                    <Button fullWidth variant="contained" color="warning" onClick={cancelWizard}>
                        {t("common.cancelWizard")}
                    </Button>

                </Box>
            </Box>
        </>
    );
};

export default ReturnWizard;
